import { Minus, TrendingDown, TrendingUp } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { StatCard } from '@nucleus-commerce/admin-kit/layout';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { computeChange } from '../schemas/analytics-range';

export interface SocialStatTileProps {
  label: string;
  /** The number itself, already formatted. */
  value: string;
  current: number;
  previous: number;
  /** Spend: more is not better, so the colours stay neutral. */
  neutral?: boolean;
  /** One more line under the delta: the organic vs paid split on Revenue. */
  breakdown?: ReactNode;
  isLoading?: boolean;
}

/** One analytics tile: the figure and its change against the previous period of equal length. */
export function SocialStatTile({
  label,
  value,
  current,
  previous,
  neutral = false,
  breakdown,
  isLoading = false,
}: SocialStatTileProps) {
  const { t } = useTranslation('social');
  const format = useFormat();
  const change = computeChange(current, previous);

  let delta: ReactNode;
  if (change.kind === 'none') {
    delta = <span className="text-muted-foreground">{t('analytics.change.none')}</span>;
  } else if (change.kind === 'flat') {
    delta = (
      <span className="text-muted-foreground inline-flex items-center gap-1">
        <Minus className="size-3" aria-hidden />
        {t('analytics.change.flat')}
      </span>
    );
  } else {
    const Icon = change.kind === 'up' ? TrendingUp : TrendingDown;
    const tone = neutral
      ? 'text-muted-foreground'
      : change.kind === 'up'
        ? 'text-success'
        : 'text-destructive';
    delta = (
      <span className={cn('inline-flex items-center gap-1', tone)}>
        <Icon className="size-3" aria-hidden />
        {t(`analytics.change.${change.kind}`, { percent: format.percent(change.ratio) })}
      </span>
    );
  }

  return (
    <div role="group" aria-label={label} className="contents">
      <StatCard
        label={label}
        value={value}
        isLoading={isLoading}
        detail={
          <span className="flex flex-col gap-0.5">
            {delta}
            {breakdown ? <span className="text-muted-foreground">{breakdown}</span> : null}
          </span>
        }
      />
    </div>
  );
}
